// src/components/Tasks/TaskTodoCards.jsx

import React, { useEffect, useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Typography,
    Button,
    CircularProgress,
    Tooltip,
    Divider,
} from '@mui/material'
import { CheckCircleOutline, OpenInNew } from '@mui/icons-material'
import { api } from '../../../services/Main/Api'
import moment from 'moment'
import { getCondoId } from '../../../services/Main/AuthStorage'

const TASKS_ENDPOINT = '/tasks/todo/'

// Mapeia cada categoria para uma cor do MUI
const CATEGORY_COLORS = {
    repasses: 'success',
    contratos: 'info',
    faturas: 'warning',
    reajustes: 'secondary',
    clientes: 'primary',
}

// Labels bonitinhos para exibir no Chip
const CATEGORY_LABELS = {
    repasses: 'Repasses',
    contratos: 'Contratos',
    faturas: 'Faturas',
    reajustes: 'Reajustes',
    clientes: 'Clientes',
}

const formatDate = (date) => {
    if (!date) return '-'
    return moment(date).format('DD/MM/YYYY')
}

const TaskTodoCards = ({ onTaskResolved }) => {
    const [tasksData, setTasksData] = useState({ results: [] })
    const [loading, setLoading] = useState(true)
    const [resolvingId, setResolvingId] = useState(null)
    const [statusFilter] = useState('pending') // ainda mantido se quiser evoluir depois

    const normalizeData = (data) => {
        if (data && Array.isArray(data.results)) return data
        if (Array.isArray(data)) return { results: data }
        return { results: [] }
    }

    const loadTasks = async () => {
        try {
            setLoading(true)
            const res = await api.get(TASKS_ENDPOINT, {
                params: { status: statusFilter, condo_id: getCondoId() },
            })
            setTasksData(normalizeData(res.data))
        } catch (e) {
            console.error('Erro ao carregar tarefas', e)
            setTasksData({ results: [] })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadTasks()

    }, [])

    const handleMarkResolved = async (taskId) => {
        try {
            setResolvingId(taskId)
            await api.post(`/tasks/${taskId}/resolve/`)

            // recarrega a lista para ficar alinhado com o back
            await loadTasks()

            // avisa o pai para atualizar o contador
            if (onTaskResolved) {
                await onTaskResolved()
            }
        } catch (e) {
            console.error(e)
        } finally {
            setResolvingId(null)
        }
    }

    const list = tasksData.results || []

    return (
        <Box sx={{ p: 1, backgroundColor: '#f7f7f7' }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2, px: 1 }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Alertas
                </Typography>
                <Button href="/tarefas/" size="small">
                    ver todas
                </Button>
            </Stack>
            <Divider sx={{ mb: 1 }} />

            {loading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : list.length === 0 ? (
                <Typography align="center" sx={{ opacity: 0.7, mt: 4 }}>
                    Nenhuma tarefa pendente 🎉
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {list.map((task) => {
                        const categoryKey = task?.category?.label
                        const categoryColor = CATEGORY_COLORS[categoryKey]
                        const categoryLabel = task?.category?.label.toLowerCase()

                        const dateToShow = task.created_at

                        return (
                            <Card
                                key={task.id}
                                sx={{
                                    borderLeft: `4px solid var(--mui-palette-${categoryColor}-main)`,
                                    borderRadius: 2,
                                }}
                            >
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {task.title}
                                        </Typography>

                                        <Chip
                                            label={categoryLabel}
                                            size="small"
                                            color={categoryColor}

                                        />
                                    </Stack>

                                    {task.description && (
                                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                                            {task.description}
                                        </Typography>
                                    )}

                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mt: 1.5, opacity: 0.7 }}
                                    >
                                        Criada em {formatDate(dateToShow)}
                                    </Typography>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Stack direction="row" spacing={1}>
                                        {task.action_url && (
                                            <Tooltip title="Abrir tela relacionada">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    endIcon={<OpenInNew />}
                                                    onClick={() =>
                                                        window.open(task.action_url, '_blank')
                                                    }
                                                >
                                                    Abrir
                                                </Button>
                                            </Tooltip>
                                        )}

                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="success"
                                            endIcon={<CheckCircleOutline />}
                                            disabled={resolvingId === task.id}
                                            onClick={() => handleMarkResolved(task.id)}
                                        >
                                            {resolvingId === task.id
                                                ? 'Salvando...'
                                                : 'Marcar como resolvida'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )
                    })}
                </Stack>
            )}
        </Box>
    )
}

export default TaskTodoCards
